import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FaCheckCircle, FaExclamationCircle, FaSpinner, FaEnvelopeOpen, FaMoon, FaSun } from 'react-icons/fa';
import Logo from "../Assets/BooksCompl.png";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setMessage('No verification token provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/verify-email?token=${token}`
        );    
        
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Email verification failed');
        }

        setMessage(data.message || 'Email verified successfully!');
        setIsSuccess(true);
        
        setTimeout(() => navigate('/login'), 3000);
      } catch (error) {
        setMessage(error.message || 'Failed to verify email');
        setIsSuccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-dark-900 transition-colors duration-300">
      <button onClick={toggleTheme} className="fixed top-6 right-6 z-50 p-3 rounded-xl bg-white dark:bg-dark-800 shadow-lg border border-gray-200 dark:border-dark-700 hover:scale-105 transition-all duration-200" aria-label="Toggle theme">
        {isDarkMode ? <FaSun className="w-5 h-5 text-yellow-500" /> : <FaMoon className="w-5 h-5 text-primary-600" />}
      </button>

      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-500/30 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        </div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img src={Logo} alt="BooksComply" className="h-16 w-auto mb-8 drop-shadow-lg" />
          <h2 className="text-4xl font-bold mb-4 text-center">Email Verification</h2>
          <p className="text-lg text-white/80 text-center max-w-md">We're verifying your email to secure your account.</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md animate-scale-in">
          <div className="lg:hidden text-center mb-8">
            <img src={Logo} alt="BooksComply" className="h-12 w-auto mx-auto" />
          </div>
          <div className="text-center mb-8">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg ${isLoading ? 'bg-gradient-to-br from-primary-500 to-primary-600' : isSuccess ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
              {isLoading ? <FaSpinner className="w-10 h-10 text-white animate-spin" /> : isSuccess ? <FaCheckCircle className="w-10 h-10 text-white" /> : <FaExclamationCircle className="w-10 h-10 text-white" />}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-dark-800 dark:text-white mb-2">Email Verification</h1>
          </div>
          <div className="text-center">
            {isLoading ? (
              <div className="space-y-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center animate-pulse">
                  <FaEnvelopeOpen className="w-6 h-6 text-primary-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">Verifying your email address...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${isSuccess ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                  <p className={`font-medium ${isSuccess ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>{message}</p>
                </div>
                {isSuccess && <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting to login page...</p>}
                {!isSuccess && <Link to="/login" className="inline-block mt-4 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 shadow-lg hover:shadow-xl transition-all duration-200">Go to Login</Link>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;